import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { BookOpen, Shield, Sparkles } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { getClassNames, cn } from '../styles/theme';
import logoHero from '../assets/logos/LL(512 x 512 px).png';

const LearnPage: React.FC = () => {
  const { theme } = useTheme();
  const classNames = getClassNames(theme);
  
  return (
    <div className={classNames.pageContainer}>
      <div className={classNames.pageContent}>
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className={classNames.heroSection}
        >
          <div className="flex justify-center mb-8">
            <Link to="/" className="cursor-pointer hover:scale-105 transition-transform">
              <img src={logoHero} alt="LegacyLayer" className="w-64 h-64 md:w-80 md:h-80 object-contain" />
            </Link>
          </div>
          <h1 className={classNames.heroTitle}>
            Learn
          </h1>
          <p className={classNames.heroSubtitle}>
            Discover how to preserve your family's legacy through blockchain technology and create lasting digital memories.
          </p>
        </motion.div>

        {/* Content Sections */}
        <div className={classNames.contentSection}>
          {/* Section 1 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className={classNames.cardPrimary}
          >
            <h2 className={classNames.sectionTitle}>
              Understanding <span className={classNames.textAccent}>Digital Legacy</span>
            </h2>
            <p className={cn(classNames.sectionText, 'mb-6')}>
              Learn how blockchain technology can help preserve your family's precious items and memories for future generations.
            </p>
            <div className={classNames.gridCols}>
              <div className="text-center p-4">
                <Shield className="w-12 h-12 text-accent-wood mx-auto mb-3" />
                <h3 className="font-semibold text-white mb-2">Secure Storage</h3>
                <p className={classNames.textSecondary}>Your items are stored securely on the blockchain</p>
              </div>
              <div className="text-center p-4">
                <Sparkles className="w-12 h-12 text-accent-wood mx-auto mb-3" />
                <h3 className="font-semibold text-white mb-2">Provenance Tracking</h3>
                <p className={classNames.textSecondary}>Track the complete history of your items</p>
              </div>
              <div className="text-center p-4">
                <BookOpen className="w-12 h-12 text-accent-wood mx-auto mb-3" />
                <h3 className="font-semibold text-white mb-2">Family Sharing</h3>
                <p className={classNames.textSecondary}>Share memories with your loved ones</p>
              </div>
            </div>
          </motion.div>

          {/* Section 2 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className={classNames.cardAccent}
          >
            <h2 className={classNames.sectionTitle}>
              Getting <span className={classNames.textAccent}>Started</span>
            </h2>
            <p className={classNames.sectionText}>
              Content coming soon... This section will provide step-by-step guides for new users.
            </p>
          </motion.div>

          {/* Section 3 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className={classNames.cardPrimary}
          >
            <h2 className={classNames.sectionTitle}>
              Advanced <span className={classNames.textAccent}>Features</span>
            </h2>
            <p className={classNames.sectionText}>
              Content coming soon... This section will cover advanced features and techniques.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default LearnPage; 